import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { KeyRound, ArrowLeft } from 'lucide-react';
import toast from 'react-hot-toast';
import { authService } from '../services/auth.service';
import { useAuth } from '../contexts/AuthContext';
import { LoadingSpinner } from '../components/common/LoadingSpinner';

export function ChangePasswordPage({ forced = false }: { forced?: boolean }) {
  const { refreshUser } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (form.newPassword !== form.confirmPassword) {
      setError('As senhas não coincidem.');
      return;
    }

    if (form.newPassword.length < 8) {
      setError('A nova senha deve ter pelo menos 8 caracteres.');
      return;
    }

    setIsLoading(true);
    try {
      await authService.changePassword(form.currentPassword, form.newPassword);
      if (forced) {
        await refreshUser();
      } else {
        toast.success('Senha alterada com sucesso!');
        navigate(-1);
      }
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Erro ao alterar senha.';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  };

  const formContent = (
    <>
      <div className="flex flex-col items-center mb-6">
        <div className="w-12 h-12 rounded-full bg-subtle flex items-center justify-center mb-3">
          <KeyRound className="w-6 h-6 text-warning" />
        </div>
        <h1 className="text-xl font-bold text-ink-primary">
          {forced ? 'Alteração de senha obrigatória' : 'Alterar Senha'}
        </h1>
        <p className="text-sm text-ink-tertiary text-center mt-1">
          {forced
            ? 'Um administrador redefiniu sua senha. Defina uma nova senha para continuar.'
            : 'Digite sua senha atual e escolha uma nova senha.'}
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Senha atual</label>
          <input
            type="password"
            value={form.currentPassword}
            onChange={(e) => setForm({ ...form, currentPassword: e.target.value })}
            className="input"
            required
            autoComplete="current-password"
          />
        </div>
        <div>
          <label className="label">Nova senha</label>
          <input
            type="password"
            value={form.newPassword}
            onChange={(e) => setForm({ ...form, newPassword: e.target.value })}
            className="input"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>
        <div>
          <label className="label">Confirmar nova senha</label>
          <input
            type="password"
            value={form.confirmPassword}
            onChange={(e) => setForm({ ...form, confirmPassword: e.target.value })}
            className="input"
            required
            minLength={8}
            autoComplete="new-password"
          />
        </div>

        {error && <p className="text-sm text-danger">{error}</p>}

        <button type="submit" className="btn-primary w-full" disabled={isLoading}>
          {isLoading ? <LoadingSpinner size="sm" /> : 'Alterar senha'}
        </button>
      </form>
    </>
  );

  if (forced) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="bg-surface rounded-[14px] shadow-2xl w-full max-w-md mx-4 p-8 animate-in fade-in zoom-in duration-200">
          {formContent}
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-md mx-auto">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-2 text-sm text-ink-secondary hover:text-ink-primary mb-6 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
        Voltar
      </button>
      <div className="bg-surface rounded-[14px] shadow-sm border border-border p-8">
        {formContent}
      </div>
    </div>
  );
}

export default ChangePasswordPage;
